import React from 'react';
import { useLoading } from '../context/LoadingContext';

/**
 * Global Loader overlay matching the ApurvaX Payroll blue-600 theme.
 * Displays:
 * 1. A glowing top progress bar across the screen.
 * 2. A floating glassmorphic card with the ApurvaX shield logo and spinning indicator.
 */
const GlobalLoader = () => {
  const { isLoading, message } = useLoading();

  if (!isLoading) return null;

  return (
    <>
      {/* Top glowing progress line */}
      <div className="fixed top-0 left-0 right-0 h-1 z-[100000] overflow-hidden bg-blue-100/80 pointer-events-none">
        <div className="h-full w-full bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-400 animate-global-progress origin-left shadow-[0_0_12px_rgba(37,99,235,0.7)]" />
      </div>

      {/* Center modal card with backdrop */}
      <div
        className="fixed inset-0 z-[99999] flex items-center justify-center bg-slate-900/30 backdrop-blur-[3px] transition-all duration-300 animate-in fade-in"
        role="status"
        aria-live="polite"
      >
        <div className="relative bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-gray-100 p-6 flex flex-col items-center space-y-4 max-w-xs w-full mx-4">
          {/* Animated Spinner with ApurvaX Shield Logo */}
          <div className="relative flex items-center justify-center w-16 h-16">
            <div className="absolute inset-0 rounded-full bg-blue-100/60 animate-ping opacity-40" />
            <div className="absolute inset-0 rounded-full border-4 border-blue-100" />
            <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-600 border-r-indigo-600 animate-spin" />
            <svg
              className="w-7 h-7 text-blue-600 drop-shadow-sm"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z" />
            </svg>
          </div>

          {/* Contextual message and animated indicator dots */}
          <div className="text-center space-y-1.5 w-full">
            <div className="flex items-center justify-center space-x-1.5">
              <span className="font-semibold text-gray-900 text-sm">
                {message || "Loading..."}
              </span>
              <span className="inline-flex space-x-1 items-center pt-0.5">
                <span className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-bounce [animation-delay:-0.3s]" />
                <span className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-bounce [animation-delay:-0.15s]" />
                <span className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-bounce" />
              </span>
            </div>
            <p className="text-xs text-gray-500">
              ApurvaX Payroll • Syncing data
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default GlobalLoader;
